import React, { useEffect, useState } from 'react';
import { get } from '@grubhubprod/umami-library/dist/nodash/get';
import { Copy } from '@grubhubprod/gh-cookbook-react';
import {
  useSignupStatus,
  SignupStep,
} from 'src/components/selfService/features/Signup/context/SignupStatusProvider/SignupStatusProvider';
import {
  mapToFormValues,
  getWeekLocDates,
  getFirstInstanceDateFormatted,
  getDayLocFrequency,
} from 'src/models/LineOfCreditModelUtils';
import { ConfirmationStepCardContainer } from 'src/components/selfService/features/Signup/steps/ConfirmationStep/ConfirmationStepCardContainer/ConfirmationStepCardContainer';
import { ConfirmationSummaryRow } from 'src/components/selfService/features/Signup/steps/ConfirmationStep/ConfirmationSummaryRow/ConfirmationSummaryRow';
import { sdk } from 'src/utilities/sdk';

interface ILocDetails {
  name: string;
  amount: string;
  isRepeating: boolean;
  budgetLength: string;
  dateString: string;
  frequencyString: string;
}

interface ILineOfCreditConfirmationCardProps {
  onDataLoaded: () => void;
}

export function LineOfCreditConfirmationCard({
  onDataLoaded,
}: ILineOfCreditConfirmationCardProps): JSX.Element {
  const { corpId, getStepNumber } = useSignupStatus();
  const [hasError, setHasError] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [locDetails, setLocDetails] = useState<ILocDetails>();

  async function getLineOfCreditDetails() {
    setHasError(false);
    setIsLoadingData(true);
    try {
      const { results } = await sdk.http.get(
        `/corps/${corpId}/eventmetadata/search?filter_by=ONLY_ACTIVE`
      );

      if (get(results, 'length') > 0) {
        const locDetails = results[0];

        const { frequency, creditName, budgetLength, amount } =
          mapToFormValues(locDetails);

        let dateString = '';
        let frequencyString = 'Does not repeat';
        const isRepeating = frequency === 'repeats';

        if (budgetLength === 'week') {
          dateString = getWeekLocDates({
            loc: locDetails,
            format: 'MM/dd/yyyy',
            useDashForDateRange: true,
            useFullDates: true,
          });

          if (isRepeating) {
            frequencyString = 'Repeats every week';
          }
        } else if (budgetLength === 'day') {
          dateString = getFirstInstanceDateFormatted(
            locDetails.first_instance_utc,
            'MM/dd/yyyy'
          );

          if (isRepeating) {
            const { short } = getDayLocFrequency(locDetails);

            frequencyString = `Repeats ${short}`;
          }
        }

        setLocDetails({
          name: creditName,
          amount,
          isRepeating,
          budgetLength,
          dateString,
          frequencyString,
        });
      }
    } catch (error) {
      setHasError(true);
    } finally {
      onDataLoaded();
      setIsLoadingData(false);
    }
  }

  useEffect(() => {
    if (corpId) {
      getLineOfCreditDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpId]);

  useEffect(() => {
    if (locDetails) {
      onDataLoaded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locDetails]);

  function getDateLabel(): string {
    if (locDetails) {
      const { budgetLength, isRepeating } = locDetails;
      if (budgetLength === 'week') {
        return 'Dates';
      }

      if (budgetLength === 'day' && isRepeating) {
        return 'Starts on';
      }

      if (budgetLength === 'day' && !isRepeating) {
        return 'Date';
      }

      return '';
    }

    return '';
  }

  return (
    <ConfirmationStepCardContainer
      stepName="Meal credit"
      isLoadingData={isLoadingData}
      hasError={hasError}
      reloadData={getLineOfCreditDetails}
      linkText={locDetails ? 'Edit' : 'Add'}
      stepNumber={getStepNumber(SignupStep.BUDGET)}
      useSecondaryText={!locDetails}
    >
      {locDetails ? (
        <>
          <ConfirmationSummaryRow label="Name">
            {locDetails.name}
          </ConfirmationSummaryRow>
          <ConfirmationSummaryRow label="Amount">
            {`${locDetails.amount}/person`}
          </ConfirmationSummaryRow>
          <ConfirmationSummaryRow label={getDateLabel()}>
            {locDetails.dateString}
          </ConfirmationSummaryRow>
          <ConfirmationSummaryRow label="Frequency">
            {locDetails.frequencyString}
          </ConfirmationSummaryRow>
        </>
      ) : (
        <Copy variant="body" color="secondaryText">
          No meal credits established. Add meal credit(s) now or later so your
          team can place orders.
        </Copy>
      )}
    </ConfirmationStepCardContainer>
  );
}
